import type { EChartsOption } from 'echarts'
import { axisTextStyle, formatCompactNumber, isFiniteNumber, richSafe, tooltipBase, tooltipHtml } from './format'
import { type ChartTheme } from './palette'

/**
 * Шаг водопада: `total` — итоговый столбец от нуля (выручка, М1, М2), `delta` — приращение от текущего уровня
 * (вычеты отрицательные, доплаты положительные).
 */
export interface WaterfallStep {
  key: string
  label: string
  value: number | null
  kind: 'total' | 'delta'
}

export interface WaterfallInput {
  steps: WaterfallStep[]
  valueFormat?: (value: number) => string
  axisFormat?: (value: number) => string
}

export interface WaterfallBar {
  step: WaterfallStep
  /** Невидимое основание. */
  base: number
  /** Видимая часть (≥ 0). */
  size: number
  /** Уровень после шага. */
  running: number
  tone: 'total' | 'up' | 'down' | 'none'
}

/**
 * Раскладка водопада: итоговые столбцы стоят от нуля и задают уровень; приращения висят от уровня вверх или
 * вниз. Пустой шаг (`null`) не двигает уровень и рисуется без столбца.
 */
export function waterfallBars(steps: WaterfallStep[]): WaterfallBar[] {
  let running = 0
  return steps.map((step) => {
    if (!isFiniteNumber(step.value)) return { step, base: running, size: 0, running, tone: 'none' as const }
    if (step.kind === 'total') {
      running = step.value
      return { step, base: Math.min(0, step.value), size: Math.abs(step.value), running, tone: 'total' as const }
    }
    const before = running
    running = before + step.value
    return {
      step,
      base: Math.min(before, running),
      size: Math.abs(step.value),
      running,
      tone: step.value >= 0 ? ('up' as const) : ('down' as const),
    }
  })
}

const SIGN_MINUS = '−'

/**
 * Ступени P&L: выручка → вычеты → М1 → реклама → М2. Полярность — расходящиеся полюса палитры (рост — синий,
 * снижение — красный), итоги — нейтральные; подпись значения со знаком над столбцом цветом текста.
 */
export function buildWaterfallOption(input: WaterfallInput, theme: ChartTheme): EChartsOption {
  const bars = waterfallBars(input.steps)
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const axisFormat = input.axisFormat ?? ((value: number) => formatCompactNumber(value))
  const [negative, , positive] = theme.diverging
  const toneColor = { total: theme.textMuted, up: positive, down: negative, none: 'transparent' }

  const signed = (bar: WaterfallBar) => {
    const value = bar.step.value
    if (!isFiniteNumber(value)) return '—'
    if (bar.step.kind === 'total') return valueFormat(value)
    return value >= 0 ? `+${valueFormat(value)}` : `${SIGN_MINUS}${valueFormat(Math.abs(value))}`
  }

  return {
    backgroundColor: 'transparent',
    animationDuration: 300,
    textStyle: { fontFamily: theme.fontFamily },
    // Describe business steps rather than ECharts' invisible stacking vectors,
    // whose missing coordinates otherwise become "NaN" for screen readers.
    aria: { enabled: true, label: { description: `Ступени показателей. ${bars.map((bar) => `${bar.step.label}: ${isFiniteNumber(bar.step.value) ? signed(bar) : 'нет данных'}`).join('. ')}.` } },
    grid: { top: 28, left: 8, right: 8, bottom: 8, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const bar = bars[params.dataIndex]
        if (!bar) return ''
        return tooltipHtml(theme, bar.step.label, [
          { color: toneColor[bar.tone], label: bar.step.kind === 'total' ? 'итог' : 'изменение', value: signed(bar) },
          ...(bar.step.kind === 'delta' ? [{ label: 'уровень после шага', value: valueFormat(bar.running) }] : []),
        ])
      },
    },
    xAxis: {
      type: 'category',
      data: bars.map((bar) => bar.step.label),
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), color: theme.textSecondary, interval: 0, width: 96, overflow: 'break' },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: number) => axisFormat(value) },
      splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      splitNumber: 4,
    },
    series: [
      {
        type: 'bar',
        stack: 'waterfall',
        stackStrategy: 'all',
        silent: true,
        itemStyle: { color: 'transparent' },
        emphasis: { disabled: true },
        tooltip: { show: false },
        data: bars.map((bar) => bar.base),
      },
      {
        type: 'bar',
        stack: 'waterfall',
        stackStrategy: 'all',
        barMaxWidth: 24,
        cursor: 'pointer',
        data: bars.map((bar) => ({
          value: bar.tone === 'none' ? '-' : bar.size,
          itemStyle: { color: toneColor[bar.tone], borderRadius: 4 },
        })),
        label: {
          show: true,
          position: 'top',
          color: theme.text,
          fontSize: 11,
          fontWeight: 600,
          fontFamily: theme.fontFamily,
          formatter: (params: any) => richSafe(signed(bars[params.dataIndex])),
        },
      },
    ],
  } as EChartsOption
}
